import { Trans, useLingui } from "@lingui/react/macro";
import {
  Button,
  PriceBoxBottomLabel,
  PriceBoxTopLabel,
  PriceValue,
  PromoBadges
} from "@silversea-ssc/ui-components-react";
import { CruiseCardModel } from "components/CruiseCard/types";
import { NewEcommerceButton } from "components/ECommerceButtons";
import React, { useEffect } from "react";
import { useECommerceStatus } from "src/ssc-api/useECommerceStatus";
import { getCruiseSpecificRAQ } from "src/utils/requestAQuoteHelper";
import * as styles from "./CruiseDrawerPriceAndOffers.module.scss";
import { arrayIsNotEmpty } from "src/utils/checks";
import clsx from "clsx";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import {
  perGuestLabel,
  transformPriceFrom,
  withLowestFareAvailableLabel
} from "components/Prices/utils";

type Props = {
  cruiseCard: CruiseCardModel;
  raqPagePath: string | undefined;
};
const CruiseDrawerPriceAndOffers: React.FC<Props> = ({
  cruiseCard,
  raqPagePath
}) => {
  const { isSticky, ref } = useInnerBarIsSticky();
  const promoBadges = cruiseCard.promosBadge;
  const ecommerceStatus = useECommerceStatus(
    cruiseCard?.voyageCode,
    cruiseCard?.available !== true
  );

  const cruiseSpecificRAQUrl =
    raqPagePath && cruiseCard.voyageCode
      ? getCruiseSpecificRAQ(raqPagePath, cruiseCard.voyageCode)
      : undefined;

  const geolocation = useGeolocation();
  const { t } = useLingui();
  const isWaitlist = cruiseCard.leadPriceFrom?.type == "waitlist";
  const transformedPrice = transformPriceFrom(
    t,
    cruiseCard.leadPriceFrom,
    geolocation.defaultCurrency,
    true,
    "only-from"
  );

  return (
    <>
      <section className={styles.promoBadges}>
        {arrayIsNotEmpty(promoBadges) && (
          <PromoBadges promos={promoBadges} variant="normal" />
        )}
      </section>
      <div ref={ref}></div>
      <section
        className={clsx(styles.priceAndOffers, { [styles.isSticky]: isSticky })}
      >
        <div>
          <div className={styles.priceRow}>
            <PriceBoxTopLabel uppercase>
              {t({ id: "from-price" })}
            </PriceBoxTopLabel>
            {transformedPrice && (
              <PriceValue
                price={transformedPrice}
                chipBackgroundColor="neutral05"
                priceType="primary"
              />
            )}
            <PriceBoxBottomLabel uppercase>
              {t(perGuestLabel())}
            </PriceBoxBottomLabel>
          </div>
          {isWaitlist ? undefined : (
            <PriceBoxBottomLabel uppercase>
              <div>{t(withLowestFareAvailableLabel())}</div>
            </PriceBoxBottomLabel>
          )}
        </div>

        <div className={styles.ctas}>
          <NewEcommerceButton
            raqUrl={cruiseSpecificRAQUrl}
            url={ecommerceStatus}
            variant="accent"
            size="small"
            overrideEcommerceLabel={
              <Trans id="ecommerce-button-label">Book your Cruise</Trans>
            }
          />
          <Button
            renderAs="a"
            href={cruiseCard.detailUrl}
            variant="secondary"
            dataContentPiece="cruise-drawer-view-itinerary"
            size="small"
          >
            <Trans>View Itinerary</Trans>
          </Button>
        </div>
      </section>
    </>
  );
};
export default CruiseDrawerPriceAndOffers;

const useInnerBarIsSticky = () => {
  const [isSticky, setIsSticky] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    const element = ref.current;
    try {
      // eslint-disable-next-line compat/compat
      observer = new IntersectionObserver(
        ([e]) => {
          setIsSticky(!e.isIntersecting);
        },
        { threshold: [1] }
      );

      if (element) {
        observer.observe(element);
      }
    } catch (e) {
      console.error("IntersectionObserver not supported", e);
    }
    return () => {
      if (element) observer?.unobserve(element);
    };
  }, []);

  return { isSticky, ref };
};
